import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { resumeUpload } from '../../../supports/resumeUpload';
import unifiedAuthManager from '../../../services/unifiedAuthManager';
import { RecruiterApi } from '../../../supports/recruiterApi';
import { generateRequestId } from '../../../common/common';
import { FiX, FiPlus, FiTrash2, FiCheck, FiChevronDown } from "react-icons/fi";
import { Maximize,ArrowLeft } from "lucide-react";
import SkillBadge from '../../../common/skillBadge';
import { useToast } from '../../../common/toastSystem';
import Loader from '../../../common/loader';
import CustomDropdown from '../../../common/dropdown';

// Constants
const apiCache = {};

const DROPDOWN_APIS = {
    'country code': async () => {
        if (apiCache['country code']) return apiCache['country code'];
        const res = await resumeUpload.getCountryCodes({
            user_payload: { request_id: generateRequestId() },
        });
        const data = res?.success ? res.data || [] : [];
        apiCache['country code'] = data;
        return data;
    },
    'current location': async () => {
        if (apiCache['nationalities']) return apiCache['nationalities'];
        const res = await resumeUpload.getCountryGuid({
            user_payload: { request_id: generateRequestId() },
        });
        const data = res?.success ? res.data || [] : [];
        apiCache['nationalities'] = data;
        return data;
    },
    'nationality': async () => {
        if (apiCache['nationalities']) return apiCache['nationalities'];
        const res = await resumeUpload.getCountryGuid({
            user_payload: { request_id: generateRequestId() },
        });
        const data = res?.success ? res.data || [] : [];
        apiCache['nationalities'] = data;
        return data;
    },
    'higher education level': async () => {
        if (apiCache['higher education level']) return apiCache['higher education level'];
        const res = await resumeUpload.getEducationLevels({
            user_payload: { request_id: generateRequestId() },
        });
        const data = res?.success ? res.data || [] : [];
        apiCache['higher education level'] = data;
        return data;
    },
    'project phase': async () => {
        if (apiCache['project phase']) return apiCache['project phase'];
        const res = await resumeUpload.getProjectPhases({
            user_payload: { request_id: generateRequestId() },
        });
        const data = res?.success ? res.data || [] : [];
        apiCache['project phase'] = data;
        return data;
    },
    'language': async () => {
        if (apiCache['language']) return apiCache['language'];
        const res = await resumeUpload.getLanguages({
            user_payload: { request_id: generateRequestId() },
        });
        const data = res?.success ? res.data || [] : [];
        apiCache['language'] = data;
        return data;
    },
    'sector': async () => {
        if (apiCache['sector']) return apiCache['sector'];
        const res = await resumeUpload.getSectors({
            user_payload: { request_id: generateRequestId() },
        });
        const data = res?.success ? res.data || [] : [];
        apiCache['sector'] = data;
        return data;
    },
    'language levels': async () => {
        if (apiCache['language levels']) return apiCache['language levels'];
        const res = await resumeUpload.getLanguageLevels({
            user_payload: { request_id: generateRequestId() },
        });
        const data = res?.success ? res.data || [] : [];
        apiCache['language levels'] = data;
        return data;
    },
    'experience level': async () => {
    if (apiCache['experience level']) return apiCache['experience level'];
    const res = await resumeUpload.getExperienceLevels({
        user_payload: { request_id: generateRequestId() },
    });
    const data = res?.success ? res.data || [] : [];
    apiCache['experience level'] = data;
    return data;
},
};

const GENDER_OPTIONS = [
    { value: 'female', label: 'Female' },
    { value: 'male', label: 'Male' },
];

const SHOW_ALL_FIELDS = false;

const NOTICE_PERIOD_MAX = { days: 365, months: 12 };

const HAS_LANGUAGE = (data) => {
    const languagesExist = Array.isArray(data.languages) && data.languages.length > 0;
    const otherLanguagesExist = Array.isArray(data.other_languages) && data.other_languages.length > 0;
    return languagesExist || otherLanguagesExist;
};

const REQUIRED_FIELDS = [
    { label: 'first_name', check: (data) => !!data.first_name },
    { label: 'gender', check: (data) => !!data.gender },
    { label: 'last_name', check: (data) => !!data.last_name },
    { label: 'date of birth', check: (data) => !!data.date_of_birth },
    { label: 'email', check: (data) => !!data.email },
    { label: 'country code', check: (data) => !!(data['country code'] ?? data.country_code) },
    { label: 'phone', check: (data) => !!data.phone },
    { label: 'nationality', check: (data) => !!data.nationality },
    // Remove "current location" from auto-detect as always ask for it
    //{ label: 'current location', check: (data) => !!(data['current location'] ?? data.location) },
    { label: 'higher education level', check: (data) => !!data.highest_education_level },
    { label: 'project phase', check: (data) => !!data.project_phase },
    { label: 'sector', check: (data) => !!data.sector_id },
    { label: 'expected salary', check: (data) => !!data.expected_salary },
    { label: 'notice period', check: (data) => !!data.availability_notice_period },
    { label: 'language', check: HAS_LANGUAGE },
    { label: 'experience level', check: (data) => !!data.experience_level },
];

const FIELDMAP = {
    'country code': 'country_code',
    'current location': 'location',
    'higher education level': 'highest_education_level',
    'date of birth': 'date_of_birth',
    'language': 'language_names',
    'sector': 'sector_id',
    'experience level': 'experience_level',
};

const fieldClass = 'border rounded px-2 py-1 w-full text-xs text-gray-700 min-h-[36px]';

// Utility functions
const truncateWithEllipsis = (str, maxLength = 24) => {
    if (!str) return "";
    return str.length > maxLength ? str.slice(0, maxLength - 1) + "…" : str;
};

const getLangProfString = (lang, options, languageLevels) => {
    if (!lang) return "";
    const langOption = options.find(l => l.code === lang.language);
    const langName = langOption?.language || lang.language || "";
    const levelOption = languageLevels.find(l => l.code === lang.proficiency);
    const proficiency = levelOption?.language || lang.proficiency || "";
    if (proficiency) {
        return `${langName} (${proficiency})`;
    }
    return langName;
};

const useDropdownData = (isOpen, missingFields) => {
    const [dropdownData, setDropdownData] = useState({});
    const [isDropdownLoading, setIsDropdownLoading] = useState(false);
    const isFetchingRef = useRef({});

    const fetchDropdowns = useCallback(async () => {
        if (!isOpen) return;
        setIsDropdownLoading(true);

        const fetchAllFields = SHOW_ALL_FIELDS
            ? REQUIRED_FIELDS.map(v => v.label).filter(f => Object.keys(DROPDOWN_APIS).includes(f))
            : missingFields.filter(f => Object.keys(DROPDOWN_APIS).includes(f));

        const fieldsToFetchSet = new Set(fetchAllFields);
        fieldsToFetchSet.add('current location');
        fieldsToFetchSet.add('nationality');
        const fieldsToFetch = Array.from(fieldsToFetchSet);

        const newDropdownData = { ...dropdownData };
        const nationalityNeeded = fieldsToFetch.includes('current location') || fieldsToFetch.includes('nationality');

        if (fieldsToFetch.includes('language')) {
            fieldsToFetch.push('language levels');
        }

        if (nationalityNeeded && !apiCache['nationalities'] && !isFetchingRef.current['nationalities']) {
            isFetchingRef.current['nationalities'] = true;
            try {
                const res = await resumeUpload.getCountryGuid({ user_payload: { request_id: generateRequestId() } });
                const data = res?.success ? res.data || [] : [];
                apiCache['nationalities'] = data;
                newDropdownData['current location'] = data;
                newDropdownData['nationality'] = data;
            } catch (err) {
                console.error('Error fetching nationalities:', err);
            } finally {
                isFetchingRef.current['nationalities'] = false;
            }
        } else if (nationalityNeeded && apiCache['nationalities']) {
            newDropdownData['current location'] = apiCache['nationalities'];
            newDropdownData['nationality'] = apiCache['nationalities'];
        }

        const fetches = fieldsToFetch.filter(
            f => !['current location', 'nationality'].includes(f) && !apiCache[f]
        ).map(async field => {
            const apiFn = DROPDOWN_APIS[field];
            if (!apiFn || isFetchingRef.current[field]) return;
            isFetchingRef.current[field] = true;
            try {
                const data = await apiFn();
                newDropdownData[field] = data;
            } catch (e) {
                console.error(`Error fetching ${field}:`, e);
            } finally {
                isFetchingRef.current[field] = false;
            }
        });

        await Promise.all(fetches);
        fieldsToFetch.forEach(field => {
            if (apiCache[field] && !newDropdownData[field]) {
                newDropdownData[field] = apiCache[field];
            }
        });

        setDropdownData(prev => ({ ...prev, ...newDropdownData }));
        setIsDropdownLoading(false);
    }, [isOpen, JSON.stringify(missingFields)]);

    useEffect(() => {
        fetchDropdowns();
    }, [fetchDropdowns]);

    return { dropdownData, isDropdownLoading };
};

// Main Component
const FillMandatoryFieldsModal = ({
    isOpen,
    onClose,
    candidate,
    onCandidateSubmitted,
    mph_jobid
}) => {
    const [formData, setFormData] = useState({ ...candidate, ['current location']: '' });
    const [languageSelections, setLanguageSelections] = useState([]);
    const [dobNA, setDobNA] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [noticePeriodType, setNoticePeriodType] = useState('days');
    const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
    const languageDropdownRef = useRef(null);
    const user = unifiedAuthManager.getUnifiedUser();
    const toast = useToast()
    const [isClientNominee, setIsClientNominee] = useState(false);
    const [forecastedMobDate, setForecastedMobDate] = useState('');
    const [isCommentExpanded, setIsCommentExpanded] = useState(false);
    const commentTextareaRef = useRef(null);
    const getMissingFields = useCallback((data) => {
        return REQUIRED_FIELDS
            .filter(fieldDef => {
                if (fieldDef.label === 'current location' || fieldDef.label === 'notice period' || fieldDef.label === 'expected salary') return true;
                if (fieldDef.label === 'date of birth') {
                    if (data.date_of_birth) return false;
                }
                if (fieldDef.label === 'language') {
                    const languagesExist = Array.isArray(data.languages) && data.languages.length > 0;
                    const otherLanguagesExist = Array.isArray(data.other_languages) && data.other_languages.length > 0;
                    return !(languagesExist || otherLanguagesExist);
                }
               if (fieldDef.label === 'years of experience') {
    if (data['years_of_experience'] !== undefined && data['years_of_experience'] !== '') return false;
}
                return !fieldDef.check(data);
            })
            .map(fieldDef => fieldDef.label)
            .concat(['current location'])
            .filter((v, i, arr) => arr.indexOf(v) === i);
    }, []);

    // Initialize form data and missing fields
    const [missingFields, setMissingFields] = useState([]);
    useEffect(() => {

        let initialProjectPhase = candidate.project_phase || [];
        if (!Array.isArray(initialProjectPhase) && initialProjectPhase) {
          initialProjectPhase = [initialProjectPhase];
        }

        let initialData = { ...candidate, ['current location']: '',['project phase']: initialProjectPhase  };
        setFormData(initialData);
        let langSelections = [];
        let addedLangCodes = new Set();

        if (Array.isArray(candidate.languages) && candidate.languages.length > 0) {
            candidate.languages.forEach(lang => {
                let obj = typeof lang === 'object' ? lang : { language: lang, proficiency: 'basic' };
                if (obj.language && !addedLangCodes.has(obj.language)) {
                    langSelections.push(obj);
                    addedLangCodes.add(obj.language);
                }
            });
        }

        if (Array.isArray(candidate.other_languages) && candidate.other_languages.length > 0) {
            candidate.other_languages.forEach(lang => {
                let obj = typeof lang === 'object' ? lang : { language: lang, proficiency: 'basic' };
                if (obj.language && !addedLangCodes.has(obj.language)) {
                    langSelections.push(obj);
                    addedLangCodes.add(obj.language);
                }
            });
        }

        setLanguageSelections(langSelections);
        setDobNA(candidate.date_of_birth);
        setMissingFields(getMissingFields(initialData));
    }, [candidate, isOpen, getMissingFields]);

    useEffect(() => {
        if (!isOpen) return;
        let val = candidate.notice_period || '';
        if (typeof val === 'string' && val.toLowerCase().includes('month')) setNoticePeriodType('months');
        else if (typeof val === 'string' && val.toLowerCase().includes('day')) setNoticePeriodType('days');
        else if (!isNaN(Number(val))) setNoticePeriodType(Number(val) <= 90 ? 'days' : 'months');
    }, [candidate.notice_period, isOpen]);

    const { dropdownData, isDropdownLoading } = useDropdownData(isOpen, missingFields);

    useEffect(() => {
        if (!showLanguageDropdown) return;
        const handleClick = (e) => {
            if (languageDropdownRef.current && !languageDropdownRef.current.contains(e.target)) {
                setShowLanguageDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [showLanguageDropdown]);

    // Auto-expand comment if length > 500 characters
    useEffect(() => {
        const commentLength = formData.comments ? formData.comments.length : 0;
        if (commentLength > 500 ) {
            setIsCommentExpanded(true);
        } 
        else if (commentLength <= 500 && !isCommentExpanded) {
            setIsCommentExpanded(false);
        }
    }, [formData.comments]);


     useEffect(() => {
        const commentLength = formData.comments ? formData.comments.length : 0;
        if (commentLength > 500 && isCommentExpanded) {
            // Focus at the end of the text when auto-expanding
            setTimeout(() => {
                if (commentTextareaRef.current) {
                    commentTextareaRef.current.focus();
                    const textLength = commentTextareaRef.current.value.length;
                    commentTextareaRef.current.setSelectionRange(textLength, textLength);
                }
            }, 50);
        } 
    }, [ isCommentExpanded]);

    const handleChange = useCallback((field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    }, []);


    const handleCountryCodeChange = useCallback((e) => handleChange('country code', e.target.value), [handleChange]);
    

     const handleExpectedSalaryInputChange = useCallback((e) => {
        const value = e.target.value;
        // Allow: letters, numbers, spaces, and valid currency symbols/formats: $ € £ ¥ ₹ , . -
        // Reject: unsupported special characters like @ % _ # * & ( ) [ ] { } etc.
        const validPattern = /^[a-zA-Z0-9\s$€£¥₹,.\-]*$/;
        
        if (validPattern.test(value)) {
            setFormData(prev => ({ ...prev, expected_salary: value }));
        }
        // If invalid characters are present, the input is silently rejected (no state update)
    }, []);

    
    const handleNoticePeriodInputChange = useCallback((e) => {
        let value = e.target.value.replace(/[^\d]/g, '');
        let periodType = noticePeriodType;
        let max = NOTICE_PERIOD_MAX[periodType] || 365;
        value = value.replace(/^0+(?!$)/, '');
        let numVal = parseInt(value, 10);
        if (isNaN(numVal)) numVal = '';
        if (numVal !== '') {
            if (numVal > max) numVal = max;
            if (numVal < 0) numVal = '';
        }
        setFormData(prev => ({ ...prev, notice_period: numVal === '' ? '' : String(numVal) }));
    }, [noticePeriodType]);
    const handleNoticePeriodTypeChange = useCallback((e) => {
        const newType = e.target.value;
        setNoticePeriodType(newType);
        setFormData(prev => {
            let curValue = prev.notice_period || "";
            let numVal = parseInt(curValue, 10);
            let max = NOTICE_PERIOD_MAX[newType] || 365;
            if (!isNaN(numVal)) {
                if (numVal > max) numVal = max;
                if (numVal < 0) numVal = '';
            } else {
                numVal = '';
            }
            return { ...prev, notice_period: numVal === '' ? '' : String(numVal) };
        });
    }, []);
    const handlePhoneInputChange = useCallback((e) => {
        const inputValue = e.target.value;
        const numbersOnly = inputValue.replace(/[^0-9+\-\s()]/g, '');
        setFormData(prev => ({ ...prev, phone: numbersOnly }));
    }, []);
    const handleAddLanguage = useCallback(() => {
        setLanguageSelections((prev) => [...prev, { language: '', proficiency: '', isNew: true }]);
    }, []);
    const handleLanguageChange = useCallback((idx, key, value) => {
        setLanguageSelections((prev) =>
            prev.map((item, i) => (i === idx ? { ...item, [key]: value } : item))
        );
    }, []);
    const handleRemoveLanguage = useCallback((idx) => {
        setLanguageSelections((prev) => prev.filter((_, i) => i !== idx));
    }, []);
    const handleDeselectConfirmedLanguage = useCallback((langIdxInConfirmed) => {
        const confirmedLangs = languageSelections
            .map((l, i) => ({ ...l, _idx: i }))
            .filter(l => l.language && l.proficiency && !l.isNew);

        const selIdx = confirmedLangs[langIdxInConfirmed]?._idx;
        if (typeof selIdx === 'number') {
            setLanguageSelections(prev => prev.filter((_, i) => i !== selIdx));
        }
    }, [languageSelections]);
    const handleDeselectLanguageValue = useCallback((idx) => {
        setLanguageSelections((prev) =>
            prev.map((item, i) => i === idx ? { ...item, language: '' } : item)
        );
    }, []);
    const handleSaveLanguage = useCallback((idx) => {
        const lang = languageSelections[idx];
        if (lang.language && lang.proficiency) {
            setLanguageSelections((prev) =>
                prev.map((item, i) => (i === idx ? { ...item, isNew: false } : item))
            );
        }
    }, [languageSelections]);
    const handleDobNAChange = useCallback((e) => {
        const checked = e.target.checked;
        setDobNA(checked);
        setFormData((prev) => ({ ...prev, date_of_birth: checked ? null : '' }));
    }, []);
    const handleDobInputChange = useCallback((e) => {
        setDobNA(false);
        setFormData((prev) => ({ ...prev, date_of_birth: e.target.value }));
    }, []);
    const handleYearsOfExperienceInputChange = useCallback((e) => {
        let value = e.target.value.replace(/[^\d]/g, '');
        if (value !== '') value = String(Math.max(0, parseInt(value, 10)));
        setFormData(prev => ({ ...prev, years_of_experience: value }));
    }, []);
    // Validation
    const isValueFilled = useCallback((val) => {
        if (typeof val === 'string') return val.trim() !== '';
        return (val !== undefined && val !== null && val !== '');
    }, []);
    const areAllFieldsFilled = useCallback(() => {
        let nationality_ok =
            !!formData['nationality'] ||
            !!formData[FIELDMAP['nationality']] ||
            !!formData['nationality_name'] ||
            !!formData['nationality_country_guid'];

        // Always require a value for 'current location'
        let currentLocation_ok =
            !!formData['current location'];

        if (!nationality_ok || !currentLocation_ok) return false;

        const fieldsToCheck = missingFields;

        for (const field of fieldsToCheck) {
            if (field === 'current location') {
                if (!isValueFilled(formData['current location'])) return false;
                continue;
            }
            if (field.toLowerCase() === 'language') {
                if (!languageSelections.length) return false;
                for (const lang of languageSelections) {
                    if (!isValueFilled(lang.language) || !isValueFilled(lang.proficiency) || (lang.isNew )) {
                            return false;
                    }
                }
                continue;
            }
            if (field.toLowerCase() === 'date of birth') {
                const dob = formData['date_of_birth'];
                if (dobNA) continue;
                if (!isValueFilled(dob)) return false;
                continue;
            }
            if (field === 'experience level') {
    if (!formData['experience_level'] && !formData['experience level']) return false;
    continue;
}
            if (field === 'expected salary') {
                if (!formData['expected_salary']) return false;
                continue;
            }
            if (field === 'notice period') {
                if (!formData['notice_period'] || !/^\d+$/.test(formData['notice_period']))
                    return false;
                const noticeNum = parseInt(formData['notice_period'], 10);
                if (
                    noticePeriodType === 'days' &&
                    (isNaN(noticeNum) || noticeNum < 0 || noticeNum > NOTICE_PERIOD_MAX.days)
                )
                    return false;
                if (
                    noticePeriodType === 'months' &&
                    (isNaN(noticeNum) || noticeNum < 0 || noticeNum > NOTICE_PERIOD_MAX.months)
                )
                    return false;
                continue;
            }

            if (field === 'project phase') {
    const val = formData['project phase'] || formData.project_phase;
    if (Array.isArray(val)) {
        if (val.length === 0) return false;
    } else {
        if (!isValueFilled(val)) return false;
    }
    continue;
}
            if (!isValueFilled(formData[field]) && !isValueFilled(formData[FIELDMAP[field] || field]))
                return false;
        }
        if (isClientNominee && !forecastedMobDate) {
        return false; // Forecasted MOB Date is mandatory when Client Nominee is ON
      }
        return true;
    }, [formData, languageSelections, dobNA, missingFields, noticePeriodType, isClientNominee, forecastedMobDate, isValueFilled]);
    const isSaveDisabled = useMemo(() => isSaving || !areAllFieldsFilled() || isDropdownLoading, [isSaving, areAllFieldsFilled, isDropdownLoading]);
    // Helper functions
    const getDropdownOptionForField = useCallback((label, value, dropdownData) => {
        const options = dropdownData[label] || [];
        if (!value) return undefined;
        let match = options.find(
            option =>
                option.country_guid === value ||
                option.name === value ||
                option.country_name === value ||
                option.value === value ||
                option.code === value
        );
        if (!match && value && typeof value === 'object' && value.country_guid) {
            match = options.find(option => option.country_guid === value.country_guid);
        }
        return match;
    }, []);
    const getAvailabilityFromNoticePeriod = useCallback((notice, type) => {
        if (!notice) return formData.availability_notice_period;
        notice = String(notice);
        if (!/^\d+$/.test(notice)) return '';
        return `${notice} ${type}`;
    }, []);
    
    // Get changed fields for API
    const getChangedFieldsAndVars = useCallback((shownFieldsList, missingFieldsList) => {
        const changedVars = [];
        const changedValues = {};
        const baseCandidate = candidate || {};

        const fieldsToIterate = [
            ...shownFieldsList,
            ...missingFieldsList.filter(fld =>
                ['nationality', 'current location', 'years of experience'].includes(fld) && !shownFieldsList.includes(fld)
            ),
        ];

        // Remove duplicates
        const submitFields = Array.from(new Set(fieldsToIterate));
        const getFinalApiKey = (field) => FIELDMAP[field] || field;

        // Build languages from selected (if 'language' present)
        if (submitFields.includes('language')) {
            let currentLangArr = Array.isArray(languageSelections)
                ? languageSelections.filter(l => l.language && l.proficiency)
                : [];

            let origLangArr = [];
            let seenLang = new Set();
            if (Array.isArray(baseCandidate.languages)) {
                for (const lang of baseCandidate.languages) {
                    let obj = typeof lang === "object" ? lang : { language: lang, proficiency: "basic" };
                    if (obj.language && !seenLang.has(obj.language)) {
                        origLangArr.push(obj);
                        seenLang.add(obj.language);
                    }
                }
            }
            if (Array.isArray(baseCandidate.other_languages)) {
                for (const lang of baseCandidate.other_languages) {
                    let obj = typeof lang === "object" ? lang : { language: lang, proficiency: "basic" };
                    if (obj.language && !seenLang.has(obj.language)) {
                        origLangArr.push(obj);
                        seenLang.add(obj.language);
                    }
                }
            }

            const arrEq = (a, b) => {
                if (!Array.isArray(a) || !Array.isArray(b)) return false;
                if (a.length !== b.length) return false;
                for (let i = 0; i < a.length; i++) {
                    if (!a[i] || !b[i]) return false;
                    if (
                        String(a[i].language) !== String(b[i].language) ||
                        String(a[i].proficiency) !== String(b[i].proficiency)
                    ) {
                        return false;
                    }
                }
                return true;
            };

            if (!arrEq(currentLangArr, origLangArr)) {
                changedVars.push("languages");
                changedValues["languages"] = currentLangArr.map((lang) => ({
                    language_code: String(lang.language),
                    level_code: String(lang.proficiency),
                }));
            }
        }

        for (const uiField of submitFields) {
            if (uiField === 'languages' || uiField === 'language' || uiField === 'expected_salary') continue;
            const apiKey = getFinalApiKey(uiField);

            let curValue =
                formData[uiField] !== undefined
                    ? formData[uiField]
                    : formData[apiKey];

            let prevValue =
                baseCandidate[uiField] !== undefined
                    ? baseCandidate[uiField]
                    : baseCandidate[apiKey];

            // Special: country_code
            if (apiKey === "country_code" && dropdownData["country code"]) {
                const curOpt = getDropdownOptionForField("country code", curValue, dropdownData);
                const prevOpt = getDropdownOptionForField("country code", prevValue, dropdownData);
                const curCode = curOpt?.code || curValue || "";
                const prevCode = prevOpt?.code || prevValue || "";
                if (curCode !== prevCode) {
                    changedVars.push("country_code");
                    changedValues["country_code"] = parseInt(curCode);
                }
                continue;
            }

            // Special: sector_id
            if (apiKey === "sector_id" && dropdownData["sector"]) {
                const curOpt = getDropdownOptionForField("sector", curValue, dropdownData);
                const prevOpt = getDropdownOptionForField("sector", prevValue, dropdownData);
                const curSId = curOpt?.id ?? curOpt?.value ?? curOpt?.code ?? curValue ?? '';
                const prevSId = prevOpt?.id ?? prevOpt?.value ?? prevOpt?.code ?? prevValue ?? '';
                if (String(curSId) !== String(prevSId)) {
                    changedVars.push("sector_id");
                    changedValues["sector_id"] = curSId !== '' ? (isNaN(curSId) ? curSId : Number(curSId)) : curSId;
                }
                continue;
            }

            // Special: highest_education_level
            if (apiKey === "highest_education_level" && dropdownData["higher education level"]) {
                const curOpt = getDropdownOptionForField("higher education level", curValue, dropdownData);
                const prevOpt = getDropdownOptionForField("higher education level", prevValue, dropdownData);
                const curHId = curOpt?.code ?? curOpt?.value ?? curOpt?.id ?? curValue ?? '';
                const prevHId = prevOpt?.code ?? prevOpt?.value ?? prevOpt?.id ?? prevValue ?? '';
                if (String(curHId) !== String(prevHId)) {
                    changedVars.push("highest_education_level");
                    changedValues["highest_education_level"] = parseInt(curHId);;
                }
                continue;
            }

            // Special: project_phase
            if (apiKey === "project phase" && dropdownData["project phase"]) {
                const toArrayInt = v => {
                    if (Array.isArray(v)) return v.map(val => parseInt(val, 10)).filter(val => !isNaN(val));
                    if (v !== undefined && v !== null && v !== '') {
                        const n = parseInt(v, 10);
                        return isNaN(n) ? [] : [n];
                    }
                    return [];
                };
                const curArr = toArrayInt(curValue);
                const prevArr = toArrayInt(prevValue);
                const isSame = curArr.length === prevArr.length && curArr.every((v, i) => v === prevArr[i]);
                if (!isSame) {
                    changedVars.push("project_phase");
                    changedValues["project_phase"] = curArr;
                }
                continue;
            }

            // === Special: date_of_birth handling ===
            if (apiKey === "date_of_birth") {
                // For DOB, pass it to the API if it's different (simple string compare here)
                if (
                    (curValue !== undefined && curValue !== null && curValue !== '' && curValue !== prevValue) ||
                    ((curValue === '' || curValue === undefined || curValue === null) &&
                        (prevValue !== '' && prevValue !== undefined && prevValue !== null))
                ) {
                    changedVars.push(apiKey);
                    changedValues[apiKey] = curValue;
                }
                continue;
            }

            if (
                (curValue !== undefined && curValue !== null && curValue !== '' && curValue !== prevValue) ||
                ((curValue === '' || curValue === undefined || curValue === null) &&
                    (prevValue !== '' && prevValue !== undefined && prevValue !== null))
            ) {
                changedVars.push(apiKey);
                changedValues[apiKey] = curValue;
            }
        }

        return { changedVars, changedValues };
    }, [formData, languageSelections, candidate, dropdownData, getDropdownOptionForField]);
    const getCountryObjectFromValue = useCallback((dropdown, value) => {
        if (!dropdown || !value) return undefined;
        if (typeof value === 'object' && value.country_guid) {
            return value;
        }
        let match = dropdown.find(
            c =>
                c.country_guid === value ||
                c.name === value ||
                c.country_name === value ||
                c.value === value
        );
        return match;
    }, []);
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaveError('');
        setIsSaving(true);

        const shownFields = getShownFields();
        const allMissingFields = missingFields;

        let { changedVars, changedValues } = getChangedFieldsAndVars(shownFields, allMissingFields);
        const candidate_id = candidate.id;

        const userDob = formData["date_of_birth"];
        const candidateDob = candidate["date_of_birth"];
        if (
            (userDob !== candidateDob) &&
            !(userDob === undefined && (candidateDob === undefined || candidateDob === null || candidateDob === '')) // not changed
        ) {
            if (!changedVars.includes("date_of_birth")) changedVars.push("date_of_birth");
            changedValues["date_of_birth"] = userDob;
        }

        const countryCodesOptions = dropdownData['country code'] || [];
        const nationalityOptions = dropdownData['nationality'] || [];
        const currentLocationOptions = dropdownData['current location'] || [];

        const nationalityObj = getCountryObjectFromValue(nationalityOptions, formData['nationality']);
        const currentLocationObj = getCountryObjectFromValue(currentLocationOptions, formData['current location']);

        let passCountryCode = (() => {
            let rawVal = formData['country code'] ?? formData.country_code ?? candidate['country code'] ?? candidate.country_code ?? '';
            let match = countryCodesOptions.find(opt => opt.code === rawVal || opt.country_guid === rawVal || opt.value === rawVal);
            return match?.code || rawVal || '';
        })();
        let nationality_name = nationalityObj?.name || nationalityObj?.country_name || formData['nationality'] || '';
        let current_location_name = currentLocationObj?.name || currentLocationObj?.country_name || formData['current location'] || '';
        const availability_notice_period = getAvailabilityFromNoticePeriod(formData['notice_period'], noticePeriodType);
        if (shownFields.includes('country code') || allMissingFields.includes('country code')) {
            if (passCountryCode !== '') changedValues.country_code = parseInt(passCountryCode);
            if (!changedVars.includes('country_code') && passCountryCode !== candidate.country_code) changedVars.push('country_code');
        }
        if (shownFields.includes('notice period') || allMissingFields.includes('notice period')) {
            if (availability_notice_period) changedValues.availability_notice_period = availability_notice_period;
            if (availability_notice_period && !changedVars.includes('availability_notice_period')) changedVars.push('availability_notice_period');
        }
        if ((shownFields.includes('higher education level') || allMissingFields.includes('higher education level')) && dropdownData['higher education level']) {

            const eduOption = getDropdownOptionForField("higher education level", formData['higher education level'] || formData.highest_education_level, dropdownData);
            if (eduOption) {
                changedValues.highest_education_level = parseInt(eduOption.code);
                if (!changedVars.includes("highest_education_level")) changedVars.push("highest_education_level");
            }
        }
        if (shownFields.includes('nationality') || allMissingFields.includes('nationality')) {
            if (nationality_name) changedValues.nationality = nationality_name;
            if (!changedVars.includes("nationality")) changedVars.push("nationality");
        }
        // For current location, always take user's value
        if (shownFields.includes('current location') || allMissingFields.includes('current location')) {
            if (current_location_name) changedValues.location = current_location_name;
            if (!changedVars.includes("location")) changedVars.push("location");
        }
        let comments = null;
        if (formData.comments !== undefined && formData.comments !== null) {
            comments = formData.comments;
        }
        try {
            const res = await resumeUpload.saveResumeModifications({
                candidate_id,
                resume_modifications: changedValues,
                edited_fields: [
                    ...changedVars
                ],
                user_payload: {
                    request_id: generateRequestId(),
                    email: candidate.email || user?.email
                },
            });
            if (!res.success) {
                toast.warning(res.message || 'Failed to save candidate data. Please try again later');
                return
            }
            const expected_rate = formData['expected_salary'] ?? '';
            let submitPayloadAm = {
                candidate_id: candidate.id,
                user_payload: { request_id: generateRequestId(), email: user.email },
                mph_job_gid: mph_jobid,
                recruiter_mail: user.email,
                notice_period: availability_notice_period,
                expected_rate: expected_rate,
                nationality_name: candidate.nationality ? candidate.nationality : nationality_name,
                current_location_name: current_location_name,
                country_region_code: 5,  
                comments: comments,
            }
              submitPayloadAm.is_giftcandidate = Boolean(isClientNominee)
            if(isClientNominee){
               submitPayloadAm.forecasted_mob_date = forecastedMobDate

            }
           
            const submitRes = await RecruiterApi.submitToAM(submitPayloadAm);
            if (!submitRes || !submitRes.success) {
                setIsSaving(false);
                toast.warning(submitRes.message || 'Failed to submit candidate data. Please try again later');
                return
            }
            toast.success(submitRes.message || 'candidate submitted successfully.')
            setIsSaving(false);
            if (onCandidateSubmitted) onCandidateSubmitted(candidate.id);
            onClose(candidate.id);
        } catch (err) {
            setSaveError("Failed to submit. Please try again.");
            setIsSaving(false);
        }
    };


    const handleClientNomineeToggle = useCallback((checked) => {
     setIsClientNominee(checked);
       if (!checked) {
        setForecastedMobDate(''); // Clear date when toggle is OFF
       }
     }, []);


      const handleForecastedMobDateChange = useCallback((e) => {
      setForecastedMobDate(e.target.value);
  }, []);

    // Field rendering functions
    // const renderYearsOfExperience = useCallback(() => {
    //     if (!missingFields.includes('years of experience')) {
    //         return null;
    //     }
    //     return (
    //         <div>
    //             <label className="block text-xs font-medium text-gray-600 capitalize mb-1">
    //                 Years of Experience <span className='text-red-400 text-lg'>*</span>
    //             </label>
    //             <input
    //                 className={fieldClass}
    //                 type="number"
    //                 min={0}
    //                 step={1}
    //                 value={formData['years_of_experience'] || ''}
    //                 onChange={handleYearsOfExperienceInputChange}
    //                 placeholder="Years"
    //                 maxLength={3}
    //             />
    //         </div>
    //     );
    // }, [formData['years_of_experience'], handleYearsOfExperienceInputChange, missingFields]);

    const renderField = useCallback((label) => {
        const options = dropdownData[label] || [];

        if (label === 'date of birth') {
            let rawDob = formData['date_of_birth'] ?? formData[label];
            let dobValue = '';
            if (rawDob && String(rawDob).toUpperCase() !== 'NA') {
                if (/^\d{4}-\d{2}-\d{2}$/.test(rawDob)) {
                    dobValue = rawDob;
                } else {
                    const parsed = new Date(rawDob);
                    if (!isNaN(parsed.getTime())) {
                        dobValue = parsed.toISOString().slice(0, 10);
                    }
                }
            }
            return (
                <div className="flex flex-col">
                    <label className="block text-xs font-medium text-gray-600 capitalize mb-1">
                        Date of Birth<span className='text-red-400 text-lg'>*</span>
                    </label>
                    <div className="flex gap-2 items-center">
                        <input
                            className={fieldClass}
                            type="date"
                            value={dobNA ? '' : dobValue}
                            onChange={handleDobInputChange}
                            disabled={dobNA}
                            placeholder="YYYY-MM-DD"
                            style={{ minWidth: '150px', maxWidth: '180px' }}
                        />
                        {/* <label className="flex items-center gap-1 text-xs text-gray-700 select-none">
                            <input
                                type="checkbox"
                                checked={dobNA}
                                onChange={handleDobNAChange}
                                className="mr-1 cursor-pointer"
                            />
                            Do not disclose
                        </label> */}
                    </div>
                </div>
            );
        }
        if (label === 'project phase') {
    return (
         <div>
            <label className="block text-xs font-medium text-gray-600 capitalize mb-1">
                Project Phase <span className='text-red-400 text-lg'>*</span>
            </label>
            <CustomDropdown
                options={options}
                // Ensure value is always an array for multi-select
                value={Array.isArray(formData[label]) ? formData[label] : (formData[label] ? [formData[label]] : [])}
                onChange={(val) => handleChange(label, val)}
                optionLabel="name"
                optionValue="code"
                placeholder="Select Phases"
                isMulti={true} // Add this
            />
        </div>
    );
}
        if (label === 'gender') {
            return (
                <div>
                    <label className="block text-xs font-medium text-gray-600 capitalize mb-1">Gender<span className='text-red-400 text-lg'>*</span></label>
                    <CustomDropdown
                        options={GENDER_OPTIONS}
                        value={formData.gender || ""}
                        onChange={(val) => handleChange("gender", val)}
                        placeholder="Gender"
                        />
                </div>
            );
        }
          if (label === 'experience level') {
    return (
        <div>
            <label className="block text-xs font-medium text-gray-600 capitalize mb-1">
                Experience Level <span className='text-red-400 text-lg'>*</span>
            </label>
            <CustomDropdown
                options={options}
                value={formData['experience_level'] || formData['experience level'] || ""}
                onChange={(val) => handleChange('experience level', val)}
                optionLabel="label"   // ← shows "0-5 years", "5-10 years" etc.
                optionValue="code"    // ← sends 5, 10, 15 etc.
                placeholder="Experience Level"
            />
        </div>
    );
}


        // if (label === 'years of experience') {
        //     return renderYearsOfExperience();
        // }

        if (Object.keys(DROPDOWN_APIS).includes(label)) {
            if (label === 'country code') {
                return (
                    <div>
                        <label className="block text-xs font-medium text-gray-600 capitalize mb-1">{label}<span className='text-red-400 text-lg'>*</span></label>
                      <CustomDropdown
                        options={dropdownData["country code"] || []}
                        value={formData["country code"] || ""}
                        onChange={(val) => handleChange("country code", val)}
                        optionLabel="country_name"
                        optionValue="code"
                        placeholder="Country Code"
                        />
                    </div>
                );
            }

            if (label === 'nationality') {
                return null; // handled below
            }

            if (label === 'current location') {
                return (
                    <div>
                        <label className="block text-xs font-medium text-gray-600 capitalize mb-1">
                            Current Location<span className='text-red-400 text-lg'>*</span>
                        </label>
                      <CustomDropdown
                        options={dropdownData["current location"] || []}
                        value={formData["current location"] || ""}
                        onChange={(val) => handleChange("current location", val)}
                        optionLabel="name"
                        optionValue="country_guid"
                        placeholder="Current Location"
                        />
                    </div>
                )
            }

            if (label === 'language') {
                const langs = languageSelections.filter(l => l.language && l.proficiency && !l.isNew);
                const firstLang = langs[0];
                const restCount = langs.length > 1 ? langs.length - 1 : 0;
                const langOptions = dropdownData['language'] || [];
                const langLevels = dropdownData['language levels'] || [];

                return (
                    <div>
                        <label className="block text-xs font-medium text-gray-600 capitalize mb-1">{label}<span className='text-red-400 text-lg'>*</span></label>
                        {langs.length > 0 && (
                            <div className="flex flex-wrap items-center gap-2 mb-2 relative">
                                {firstLang && (
                                    <SkillBadge>
                                        <span className="flex items-center">
                                            <span
                                                title={getLangProfString(firstLang, langOptions, langLevels)}
                                                style={{ display: "inline-block", maxWidth: 160, verticalAlign: "bottom", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                            >
                                                {truncateWithEllipsis(getLangProfString(firstLang, langOptions, langLevels), 24)}
                                            </span>
                                            <button
                                                type="button"
                                                className="ml-1 mph-primary inline-flex items-center"
                                                style={{ padding: 0 }}
                                                title="Deselect"
                                                onClick={() => handleDeselectConfirmedLanguage(0)}
                                            >
                                                <FiX size={14} />
                                            </button>
                                        </span>
                                    </SkillBadge>
                                )}
                                {restCount > 0 && (
                                    <div className="relative" ref={languageDropdownRef}>
                                        <button
                                            type="button"
                                            className="text-xs px-2 py-1 rounded-full mph-primary mph-light flex items-center gap-1"
                                            onClick={() => setShowLanguageDropdown((v) => !v)}
                                        >
                                            +{restCount}
                                            <FiChevronDown size={14} />
                                        </button>
                                        {showLanguageDropdown && (
                                            <div className="absolute mt-2 z-20 bg-white border border-gray-200 rounded shadow-lg min-w-[180px] max-h-[4rem] overflow-auto" style={{ left: '-5rem' }}>
                                                {(langs.slice(1)).map((lang, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="flex items-center px-3 py-1 text-xs mph-primary hover:shadow-md"
                                                        title={getLangProfString(lang, langOptions, langLevels)}
                                                        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}
                                                    >
                                                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 155, display: "inline-block" }}>
                                                            {truncateWithEllipsis(getLangProfString(lang, langOptions, langLevels), 24)}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="ml-2 mph-primary inline-flex items-center"
                                                            style={{ padding: 0 }}
                                                            title="Deselect"
                                                            onClick={() => handleDeselectConfirmedLanguage(idx + 1)}
                                                        >
                                                            <FiX size={14} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {languageSelections.map((lang, idx) => {
                            if (!lang.isNew && lang.language && lang.proficiency) return null;
                            return (
                                <div key={`edit-${idx}`} className="flex gap-2 items-center mb-2">
                                    <div className="relative flex items-center gap-2">
                                      <CustomDropdown
                                            options={langOptions || []}
                                            value={lang.language || ''}
                                            onChange={(val) => handleLanguageChange(idx, 'language', val)}
                                            optionLabel="language"
                                            optionValue="code"
                                            placeholder="Language"
                                            />

                                        {/* {lang.language && (
                                            <button
                                                type="button"
                                                className="ml-[-20px] text-gray-400 hover:text-red-500 inline-flex items-center"
                                                style={{ background: 'none', border: 'none', position: 'relative', right: '18px', padding: 0, zIndex: 2 }}
                                                tabIndex={-1}
                                                title="Deselect language"
                                                onClick={() => handleDeselectLanguageValue(idx)}
                                            >
                                                <FiX size={15} />
                                            </button>
                                        )} */}
                                    </div>
                                   <CustomDropdown
                                        options={langLevels || []}
                                        value={lang.proficiency || ''}
                                        onChange={(val) => handleLanguageChange(idx, 'proficiency', val)}
                                        optionLabel="language"
                                        optionValue="code"
                                        placeholder="Proficiency"
                                        />

                                    {lang.isNew && lang.language && lang.proficiency && (
                                        <button
                                            type="button"
                                            onClick={() => handleSaveLanguage(idx)}
                                            className="text-green-600 hover:text-green-800"
                                            title="Confirm"
                                        >
                                            <FiCheck size={16} />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveLanguage(idx)}
                                        className="text-red-500 hover:text-red-700"
                                        title="Cancel"
                                    >
                                        <FiTrash2 size={16} />
                                    </button>
                                </div>
                            );
                        })}

                        <button
                            type="button"
                            onClick={handleAddLanguage}
                            className="mph-primary font-semibold text-xs flex items-center gap-1 mt-1"
                        >
                            <FiPlus /> Add Language
                        </button>
                    </div>
                );
            }
            const toTitleCase = (str) =>
            str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1));

            return (
                <div>
                    <label className="block text-xs font-medium text-gray-600 capitalize mb-1">{label}<span className='text-red-400 text-lg'>*</span></label>
                        <CustomDropdown
                            options={options}
                            value={formData[label] || ""}
                            onChange={(val) => handleChange(label, val)}
                            optionLabel="name"        // or whatever your field name is
                            optionValue="code"        // adjust to match your data shape
                            placeholder={toTitleCase(label)}  
                        />
                </div>
            );
        }

        if (label === 'last_name') {
            return (
                <div>
                    <label className="block text-xs font-medium text-gray-600 capitalize mb-1">Last Name <span className='text-red-400 text-lg'>*</span></label>
                    <input
                        className={fieldClass}
                        type="text"
                        value={formData.last_name || ''}
                        onChange={(e) => handleChange('last_name', e.target.value)}
                        placeholder="Last Name"
                    />
                </div>
            );
        }

        if (label === 'phone') {
            return (
                <div>
                    <label className="block text-xs font-medium text-gray-600 capitalize mb-1">Phone <span className='text-red-400 text-lg'>*</span></label>
                    <input
                        className={fieldClass}
                        type="tel"
                        value={formData.phone || ''}
                        onChange={handlePhoneInputChange}
                        onKeyDown={e => {
                            if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Home', 'End'].includes(e.key)) return;
                            if (/[0-9\+\-\(\)\s]/.test(e.key)) return;
                            e.preventDefault();
                        }}
                        placeholder="Enter phone number"
                        autoComplete="tel"
                        maxLength={20}
                    />
                </div>
            );
        }
      
        if (label === 'expected salary') {
            return (
                <div>
                    <label className="block text-xs font-medium text-gray-600 capitalize mb-1">
                        Expected Salary <span className='text-red-400 text-lg'>*</span>
                    </label>
                    <div className="flex items-center">
                        <input
                            className={fieldClass}
                            type="text"
                            value={formData.expected_salary || ''}
                            onChange={handleExpectedSalaryInputChange}
                            maxLength={100}
                            placeholder="Expected salary"
                            onKeyDown={e => {
                                if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)) return;
                            }}
                        />
                    </div>
                </div>
            );
        }

        if (label === 'notice period') {
            const max = noticePeriodType === 'days' ? NOTICE_PERIOD_MAX.days : NOTICE_PERIOD_MAX.months;
            const min = 0;
            return (
                <div>
                    <label className="block text-xs font-medium text-gray-600 capitalize mb-1">
                        Notice Period <span className='text-red-400 text-lg'>*</span>
                    </label>
                    <div className="flex gap-2 items-center mb-1">
                        <input
                            className={fieldClass}
                            type="number"
                            min={min}
                            max={max}
                            inputMode="numeric"
                            pattern="\d*"
                            value={formData.notice_period || ''}
                            onChange={handleNoticePeriodInputChange}
                            maxLength={`${noticePeriodType === 'days' ? 3 : 2}`}
                            placeholder={`${noticePeriodType === 'days' ? 'days' : 'months'}`}
                            onKeyDown={e => {
                                if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Home', 'End'].includes(e.key)) return;
                                if (!/\d/.test(e.key)) e.preventDefault();
                            }}
                            style={{ width: "80px", minWidth: "80px" }}
                        />
                      <CustomDropdown
                        options={[
                            { label: "Days", value: "days" },
                            { label: "Months", value: "months" },
                        ]}
                        value={noticePeriodType}
                        onChange={(val) => handleNoticePeriodTypeChange({ target: { value: val } })}
                        placeholder="Select Type"
                        className="w-[85px] min-w-[85px]"
                        />
                        <div className="hidden text-xs text-gray-400">
                            {noticePeriodType === 'days' ? '(Max: 365 days)' : '(Max: 12 months)'}
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div>
                <label className="block text-xs font-medium text-gray-600 capitalize mb-1">{label}</label>
                <input
                    className={fieldClass}
                    value={formData[label] || ''}
                    onChange={(e) => handleChange(label, e.target.value)}
                />
            </div>
        );
    }, [
        dropdownData,
        formData,
        handleChange,
        handleCountryCodeChange,
        handleDobNAChange,
        handleDobInputChange,
        handleExpectedSalaryInputChange,
        handlePhoneInputChange,
        handleYearsOfExperienceInputChange,
        handleNoticePeriodInputChange,
        handleNoticePeriodTypeChange,
        handleAddLanguage,
        handleLanguageChange,
        handleRemoveLanguage,
        handleDeselectConfirmedLanguage,
        handleDeselectLanguageValue,
        handleSaveLanguage,
        languageSelections,
        showLanguageDropdown,
        noticePeriodType,
        dobNA,
        // renderYearsOfExperience
    ]);

    const shownFields = useMemo(() => {
        return missingFields.filter(
            fld => fld !== 'nationality' && fld !== 'current location'
            //  && fld !== 'years of experience'
        );
    }, [missingFields]);
    const getShownFields = useCallback(() => {
        let arr = [...shownFields];
        if (missingFields.includes('nationality')) arr.push('nationality');
        if (!arr.includes('current location')) arr.push('current location');
        // if (missingFields.includes('years of experience')) arr.push('years of experience');
        return arr;
    }, [shownFields, missingFields]);
    const fieldRows = useMemo(() => {
        const rows = [];
        for (let i = 0; i < shownFields.length; i += 2) {
            rows.push(
                <div className="flex flex-col sm:flex-row gap-6" key={`row-${i}`}>
                    <div className="flex-1">{renderField(shownFields[i])}</div>
                    {shownFields[i + 1] !== undefined && (
                        <div className="flex-1 mt-3 sm:mt-0">
                            {renderField(shownFields[i + 1])}
                        </div>
                    )}
                </div>
            );
        }
        return rows;
    }, [shownFields, renderField]);


    const showNationality = missingFields.includes('nationality');
    const showCurrentLocation = true; // always ask
    const showYearsOfExperience = missingFields.includes('years of experience');
    const requiredDropdownKeys = useMemo(() => {
        const keys = new Set();
        const relevantFields = SHOW_ALL_FIELDS
            ? REQUIRED_FIELDS.map(f => f.label)
            : [...missingFields];
        relevantFields.forEach(fld => {
            if (Object.keys(DROPDOWN_APIS).includes(fld)) keys.add(fld);
        });
        keys.add('current location');
        keys.add('nationality');
        if (relevantFields.includes('language')) keys.add('language levels');
        return Array.from(keys);
    }, [missingFields]);

    const editFieldsReady = useMemo(() => {
        if (requiredDropdownKeys.length === 0) return true;
        for (const key of requiredDropdownKeys) {
            if (!(key in dropdownData) || dropdownData[key] == null) {
                return false;
            }
        }
        return !isDropdownLoading; 
    }, [dropdownData, requiredDropdownKeys, isDropdownLoading]);

        if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-30 flex items-center justify-center anim-slideDown">
            <div className={`
    bg-white p-4 zwischensm:p-6 rounded-xl shadow-md w-full
    max-w-md sm:max-w-xl md:max-w-[45rem]
    h-auto max-h-[95vh] transition-all
    ${isCommentExpanded ? 'overflow-hidden' : 'overflow-y-auto'}
  `}>
                {isCommentExpanded ? (
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2 pb-4 border-b border-gray-200">
                              <button
                                type="button"
                                onClick={() => setIsCommentExpanded(false)}
                                className="p-1 hover:bg-gray-100 rounded-full transition-all duration-200 text-gray-500 hover:text-gray-700"
                                title="Back to form"
                                aria-label="Close expanded comment"
                            >
                                <ArrowLeft size={16} />
                            </button>
                            <h2 className="text-sm font-semibold text-gray-800">Comment</h2>
                          
                        </div>
                        <div className="flex-1 relative">
                            <textarea
                                ref={commentTextareaRef}
                                value={formData.comments || ""}
                                onChange={(e) => handleChange("comments", e.target.value)}
                                className={`${fieldClass} resize-none focus:border-gray-200 border border-gray-200 w-full`}
                                placeholder="Add any comments here..."
                                maxLength={2000}
                                style={{
                                    minHeight: '400px',
                                    height: '80vh',
                                    overflowY: 'auto',
                                    lineHeight: '1.4rem'
                                }}
                            //     ref={(el) => {
                            //         if (el) {
                            //             el.style.height = 'auto';
                            //             const scrollHeight = el.scrollHeight;
                            //             el.style.height = `${Math.max(scrollHeight, 400)}px`;
                            //         }
                            //     }
                            // }
                                // onInput={(e) => {
                                //     e.target.style.height = 'auto';
                                //     const scrollHeight = e.target.scrollHeight;
                                //     e.target.style.height = `${Math.max(scrollHeight, 400)}px`;
                                // }}
                            />
                            <div className="absolute right-2 bottom-4 text-xs text-gray-400 bg-white/95 backdrop-blur-sm border border-gray-200/50 rounded-full px-2 py-0.5 shadow-sm">
                                {(formData.comments ? formData.comments.length : 0)}/2000
                            </div>
                            {/* <button
                                type="button"
                                onClick={() => setIsCommentExpanded(false)}
                                className="absolute bottom-4 right-2 px-4 py-2 text-sm font-semibold shadow-lg rounded-lg bg-gradient text-gray-600 hover:shadow-lg transition-all"
                            >
                                Save
                            </button> */}


                            
                        </div>
                        {/* <div className="flex gap-2 justify-end pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={() => setIsCommentExpanded(false)}
                                className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-200 text-gray-700 hover:shadow-lg transition-all"
                            >
                                Close
                            </button>
                        </div> */}
                    </div>
                ) : (
                     
                <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                    {!editFieldsReady ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader message='Loading required data'/>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-1 ">
                            {(showNationality || showCurrentLocation) && (
                                <div className="flex flex-col sm:flex-row gap-6">
                                    {showNationality && (
                                        <div className="flex-1">
                                            <label className="block text-xs font-medium text-gray-600 capitalize mb-1">
                                                Nationality <span className='text-red-400 text-lg'>*</span>
                                            </label>
                                           <CustomDropdown
                                                options={dropdownData["nationality"] || []}
                                                value={formData["nationality"] || ""}
                                                onChange={(val) => handleChange("nationality", val)}
                                                optionLabel="name"
                                                optionValue="country_guid"
                                                placeholder="Nationality"
                                                />
                                        </div>
                                    )}
                                    {showCurrentLocation && (
                                        <div className={`flex-1${showNationality ? ' mt-3 sm:mt-0' : ''}`}>
                                            <label className="block text-xs font-medium text-gray-600 capitalize mb-1">
                                                Current Location <span className='text-red-400 text-lg'>*</span>
                                            </label>
                                           <CustomDropdown
                                                options={dropdownData["current location"] || []}
                                                value={formData["current location"] || ""}
                                                onChange={(val) => handleChange("current location", val)}
                                                optionLabel="name"
                                                optionValue="country_guid"
                                                placeholder="Current Location"
                                                />

                                        </div>
                                    )}
                                </div>
                            )}
                            {/* {showYearsOfExperience && (
                                <div className="mt-3">{renderYearsOfExperience()}</div>
                            )} */}
                            {fieldRows}

                            {/* Add a comment section */}


<div className='relative'>
    <label className="block text-xs font-medium text-gray-600 capitalize mb-1 flex items-center justify-between">
      Comments
   
    </label>
    
    <div className="relative">
           {formData.comments && formData.comments.length > 0 && (
        <button
          type="button"
          onClick={() => setIsCommentExpanded(true)}
          className="absolute top-1 right-1.5 p-1 hover:bg-gray-100 rounded-full transition-all duration-200 ml-2 text-gray-500 hover:text-gray-700 flex-shrink-0"
          title="Expand comment"
          aria-label="Expand comment"
        >
       <Maximize size={10} />
        </button>
      )}

      <textarea
        ref={commentTextareaRef}
        value={formData.comments || ""}
        onChange={(e) => {
          handleChange("comments", e.target.value);
        }}
        className={`${fieldClass} pr-10 resize-none focus:border-gray-200 border border-gray-200 w-full`}
        placeholder="Add any comments here..."
        rows={7}
        maxLength={2000}
        style={{
          minHeight: '120px',
          maxHeight: '140px',
          height: 'auto',
          overflowY: 'auto',
          lineHeight: '1.4rem'
        }}
        onInput={(e) => {
          e.target.style.height = 'auto';
          const scrollHeight = e.target.scrollHeight;
          e.target.style.height = `${Math.min(Math.max(scrollHeight, 140), 200)}px`;
        }}
      />
      <div className="text-xs text-gray-400 text-right absolute bottom-3 right-2 bg-white/95 backdrop-blur-sm border border-gray-200/50 rounded-full px-2 py-0.5 shadow-sm">
        { (formData.comments ? formData.comments.length : 0)}/2000
      </div>
    </div>
  </div>


                              <div className="border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      {/* *** Forecasted MOB Date - Conditional & Mandatory when Client Nominee is ON *** */}
                      {isClientNominee && (
                        <div className="flex flex-col">
                          <label className="block text-xs font-medium text-gray-600 capitalize mb-1">
                            Forecasted MOB Date{" "}
                            <span className="text-red-400 text-lg">*</span>
                          </label>
                          <input
                            type="date"
                            value={forecastedMobDate}
                            onChange={handleForecastedMobDateChange}
                            className={`${fieldClass} `}
                            placeholder="YYYY-MM-DD"
                            min={new Date().toISOString().split("T")[0]}
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <label className="text-xs font-medium text-gray-700 flex items-center gap-2">
                        <span>Client Nominee</span>
                      </label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isClientNominee}
                          onChange={(e) =>
                            handleClientNomineeToggle(e.target.checked)
                          }
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#8ccbc9]/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#8ccbc9]"></div>
                      </label>
                    </div>
                  </div>
                </div>
                        </div>
                    )}

                    {saveError && <div className="text-xs text-red-600">{saveError}</div>}


                    <div className="flex flex-col sm:flex-row gap-3 mt-3 justify-end">
                        <button
                            disabled={isSaving}
                            type="button"
                            onClick={onClose}
                            className="px-3 py-1.5 text-xs font-semibold rounded-full bg-gray-200 text-gray-700 hover:shadow-lg"
                        >
                            Cancel
                        </button>
                        <button
                            disabled={isSaveDisabled || !editFieldsReady}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-full ${isSaveDisabled || !editFieldsReady
                                ? 'bg-gray-300 text-gray-500'
                                : 'bg-gradient text-white hover:shadow-lg'
                                }`}
                        >
                            {isSaving ? 'Submitting...' : 'Submit'}
                        </button>
                    </div>
                </form>
                )}
            </div>
        </div>
    );
};

export default FillMandatoryFieldsModal;